interface DownsampleNoticeProps {
  /**
   * stitches_removed from the response that produced the files on screen;
   * null or undefined when that isn't known (a design reopened from the
   * library), which shows nothing.
   */
  stitchesRemoved: number | null | undefined;
  /** Whether those files were asked for with downsampling (the default). */
  enabled: boolean;
  /** Called with the new value; the page re-runs its request with it. */
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

/**
 * Says so when the backend thinned out stitches a small size made too short
 * or too dense (app/services/downsample.py, behind the stitch_downsample
 * flag), with a checkbox to get the files without that. Unticking it re-runs
 * the page's request with ?downsample=false.
 *
 * Shown only when stitches were actually removed, so a design at its normal
 * size, or anyone the flag is off for, sees nothing -- and kept up once
 * unticked, even though that response removed none, so it can be ticked again.
 */
export function DownsampleNotice({ stitchesRemoved, enabled, onChange, disabled }: DownsampleNoticeProps) {
  if (stitchesRemoved == null) return null;
  const removed = stitchesRemoved;
  if (enabled && removed <= 0) return null;

  return (
    <div
      role="status"
      style={{
        border: "1px solid var(--pico-muted-border-color)",
        borderRadius: "var(--pico-border-radius)",
        padding: "0.75rem 1rem",
        marginBottom: "1rem",
        fontSize: "0.9rem",
        textAlign: "left",
      }}
    >
      <p style={{ marginBottom: "0.5rem" }}>
        {enabled
          ? `At this size some stitches were too short or too close together to sew cleanly, so ${removed.toLocaleString()} ${removed === 1 ? "was" : "were"} removed from every download.`
          : "These downloads keep every stitch. At this size some are very short or close together, which can break thread or pucker the fabric."}
      </p>
      <label style={{ marginBottom: 0 }}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={e => onChange(e.target.checked)}
        />
        Thin out dense stitches
      </label>
    </div>
  );
}
